import axios from "axios";

const BASE_URL = "https://backend.dealboxx.promo/api";

const unwrapApiEnvelope = (response) => response?.data?.data ?? response?.data ?? null;

const isCreateDealSuccess = (envelope) =>
  Boolean(envelope) &&
  String(envelope.status_code) === "001" &&
  envelope.in_error !== true;

const numericIds = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => Number(item?.id ?? item))
    .filter((id) => Number.isFinite(id));
};

const attachMarketplaceCategories = (payload, formData) => {
  const categoryIds = numericIds(
    formData.category_ids?.length ? formData.category_ids : formData.categories
  );
  const subcategoryIds = numericIds(
    formData.subcategory_ids?.length ? formData.subcategory_ids : formData.subcategories
  );

  delete payload.categories;
  delete payload.subcategories;
  delete payload.category_ids;
  delete payload.subcategory_ids;

  if (categoryIds.length) {
    payload.category_ids = categoryIds;
    delete payload.category;
    delete payload.productCategory;
    delete payload.product_category;
  }
  if (subcategoryIds.length) {
    payload.subcategory_ids = subcategoryIds;
    delete payload.product_subcategory;
  }
  return payload;
};

export class DealsRequest {
  // Optimize image before converting to base64
  static async optimizeImage(file) {
    return new Promise((resolve, reject) => {
      const maxWidth = 800; // Maximum width for the image
      const maxHeight = 800; // Maximum height for the image
      const quality = 0.7; // Image quality (0.7 = 70% quality)

      // If it's already a base64 string, return it directly
      if (typeof file === 'string' && file.startsWith('data:image/')) {
        resolve(file);
        return;
      }

      // If it's not a File object, reject
      if (!(file instanceof File)) {
        reject(new Error("Invalid file object"));
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to base64 with reduced quality
          const optimizedBase64 = canvas.toDataURL("image/jpeg", quality);
          resolve(optimizedBase64);
        };

        img.onerror = (error) => reject(error);
      };

      reader.onerror = (error) => reject(error);
    });
  }

  //Todo => Method to create a deal
  static async createDeal(formData) {
    const formatDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")} ${String(
        d.getHours()
      ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(
        d.getSeconds()
      ).padStart(2, "0")}`;
    };

    // Process primary image if it exists
    let optimizedImage = null;
    if (formData.image) {
      try {
        // If image is already a base64 string, use it directly
        if (typeof formData.image === 'string' && formData.image.startsWith('data:image/')) {
          optimizedImage = formData.image;
        }
        // If image is a File object, optimize it
        else if (formData.image instanceof File) {
          optimizedImage = await this.optimizeImage(formData.image);
        }
        // If it's neither, log a warning
        else {
          console.warn("Invalid primary image format:", typeof formData.image, formData.image);
        }
      } catch (error) {
        console.error("Primary image processing failed:", error);
        throw new Error(
          "Failed to process primary image. Please try again with a smaller image."
        );
      }
    }

    // Process gallery images if they exist
    let optimizedImages = [];
    if (formData.images && Array.isArray(formData.images) && formData.images.length > 0) {
      try {
        optimizedImages = await Promise.all(
          formData.images.map(async (image) => {
            // If image is already a base64 string, use it directly
            if (typeof image === 'string' && image.startsWith('data:image/')) {
              return image;
            }
            // If image is a File object, optimize it
            if (image instanceof File) {
              return await this.optimizeImage(image);
            }
            // If it's neither, log a warning and skip
            console.warn("Invalid image format:", typeof image, image);
            return null;
          })
        );
        // Filter out null values
        optimizedImages = optimizedImages.filter(img => img !== null);
      } catch (error) {
        console.error("Gallery images processing failed:", error);
        throw new Error(
          "Failed to process gallery images. Please try again with smaller images."
        );
      }
    }

    //* Ensuring category is an array
    const categories = Array.isArray(formData.category)
      ? formData.category
      : [formData.category];

    //* Processing region_location to use customLocation when location is 'other'
    const processedRegionLocation = formData.region_location.map((item) => ({
      ...item,
      location: item.location === "other" ? item.customLocation : item.location,
    }));

    //* Processing redemption_region_location to use customLocation when location is 'other'
    const processedRedemptionRegionLocation = formData.redemption_region_location && Array.isArray(formData.redemption_region_location)
      ? formData.redemption_region_location.map((item) => ({
          ...item,
          location: item.location === "other" ? item.customLocation : item.location,
        }))
      : [];

    //* Processing free_delivery_areas to use customLocation when location is 'other'
    const processedFreeDeliveryAreas = formData.free_delivery_areas && Array.isArray(formData.free_delivery_areas)
      ? formData.free_delivery_areas.map((item) => {
          // Handle "Nationwide" as a string (not an object)
          if (typeof item === 'string') {
            return item;
          }
          // Handle area objects with region and location
          return {
            ...item,
            location: item.location === "other" ? item.customLocation : item.location,
          };
        })
      : [];

    //* Transform properties from {key, value} to {name, value} format
    const processedProperties = formData.properties && Array.isArray(formData.properties)
      ? formData.properties.map(prop => ({
          name: prop.key,
          value: prop.value
        }))
      : [];

    //* Ensure redemption_limits are strings in array format
    const processedRedemptionLimits = formData.redemption_limits && Array.isArray(formData.redemption_limits)
      ? formData.redemption_limits.map(limit => String(limit))
      : [];

    //* Format flash data as array with object
    const flashData = [{
      flash_start_time: formData.flash_start_time || null,
      flash_end_time: formData.flash_end_time || null,
      flash_start_date: formData.flash_start_date || null,
      flash_duration: formData.flash_duration || null,
    }];

    //* Format bundle data as array with object
    const bundleData = [{
      original_sum: formData.original_sum || null,
      bundle_price: formData.bundle_price || null,
      bundle_savings: formData.bundle_savings || null,
      bundle_skus: formData.bundle_skus || [],
    }];

    //* Format bogo data as array with object
    const bogoData = [{
      bogo_item_price: formData.offer_type === 'bogo' ? (formData.bogo_item_price || null) : null,
      bogo_quantity: formData.offer_type === 'bogo' ? (formData.bogo_quantity || null) : null,
      bogo_free_quantity: formData.offer_type === 'bogo' ? (formData.bogo_free_quantity || null) : null,
    }];

    //* Format age data as array with object
    const ageData = [{
      min_age: formData.min_age || null,
      max_age: formData.max_age || null,
      no_age_limit: formData.no_age_limit || false,
    }];

    //* Preparing the payload
    const payload = {
      ...formData,
      category: categories,
      image: optimizedImage,
      images: optimizedImages,
      properties: processedProperties,
      redemption_limits: processedRedemptionLimits,
      flash: flashData,
      bundle: bundleData,
      bogo: bogoData,
      age: ageData,
      expiry_date: formatDate(formData.expiry_date),
      start_date: formatDate(formData.start_date),
      promote_deal: formData.promote_deal ? 1 : 0,
      region_location: processedRegionLocation,
      redemption_region_location: processedRedemptionRegionLocation,
      free_delivery_areas: processedFreeDeliveryAreas,
    };
    attachMarketplaceCategories(payload, formData);

    //* Making the API call
    const token = this.getToken();
    const response = await axios.post(`${BASE_URL}/vendor/deals`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const envelope = unwrapApiEnvelope(response);

    if (isCreateDealSuccess(envelope)) {
      return envelope;
    }

    throw new Error(envelope?.reason || envelope?.message || "Failed to create deal");
  }

  //Todo => Method to get all deals
  static async getAllDeals() {
    try {
      const token = this.getToken();

      if (!token) {
        return {
          success: false,
          message: "No authentication token found",
        };
      }

      const response = await axios.get(`${BASE_URL}/vendor/vendorDeals`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.data.data.status_code === "000") {
        return {
          success: true,
          data: response.data.data.data.map((deal) => ({
            id: deal.id,
            slug: deal.deal_slug,
            title: deal.title,
            product_name: deal.product_name || "",
            product_category: deal.product_category || "",
            product_subcategory: deal.product_subcategory || "",
            category: deal.category || [],
            categories: deal.categories || [],
            subcategories: deal.subcategories || [],
            status: deal.status,
            image: deal.image,
            images: deal.images || [],
            companyLogo: deal.company_logo,
            companyName: deal.company_name,
            price: deal.price,
            discountedPrice: deal.discounted_price,
            discountPercentage: deal.discount_percentage,
            createdAt: deal.created_at,
            startDate: deal.start_date,
            expiryDate: deal.expiry_date,
            region_location: deal.region_location,
            partner_location: deal.partner_location,
            redemptionType: deal.redemption_type,
            sms_delivery_status: deal.sms_delivery_status,
            subscribers_count: deal.subscriber_count,
            initial_blast: deal.initial_blast,
            terms: deal.terms,
            refundPolicy: deal.refund_policy,
            details: {
              description: deal.description,
              redemptionInstructions: deal.redemption_instructions,
            },
            promote_deal: deal.promote_deal,
            deal_slug: deal?.deal_slug,
            contact: {
              email: deal.contact?.email,
              phoneNumber: deal.phone_number,
              website: deal.online_link,
              socialMedia: deal.contact?.social_media,
            },
            // New fields from updated structure
            gender: deal.gender,
            sms_content: deal.sms_content,
            sms_count: deal.sms_count,
            vendor_id: deal.vendor_id,
            target_branches: deal.target_branches,
            redemption_region_location: deal.redemption_region_location,
            redemption_branches: deal.redemption_branches,
            redemption_partners: deal.redemption_partners,
            redemption_limits: deal.redemption_limits,
            exclusions: deal.exclusions || [],
            sku: deal.sku,
            properties: deal.properties || [],
            age: deal.age || [],
            delivery_fee: deal.delivery_fee,
            free_delivery_areas: deal.free_delivery_areas || [],
            offer_type: deal.offer_type,
            reject_reason: deal.reject_reason,
            rejected_date: deal.rejected_date,
            deleted_at: deal.deleted_at,
            quantity: deal.quantity,
            sizes: deal.sizes || [],
            // Bundle offer details
            bundle: deal.bundle || [],
            // BOGO offer details
            bogo: deal.bogo || [],
            // Flash sale details
            validity_type: deal.validity_type,
            flash: deal.flash || [],
          })),
        };
      }

      return {
        success: false,
        message: response.data.data.message || "Failed to fetch deals",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while fetching deals",
      };
    }
  }

  //Todo=> Add method to handle pagination if needed
  static async getDealsByPage(page = 1, limit = 10) {
    try {
      const token = this.getToken();

      const response = await axios.get(`${BASE_URL}/vendor/deals`, {
        params: {
          page,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (response.data.data.status_code === "000") {
        return {
          success: true,
          data: response.data.data.data.map((deal) => ({
            // Same transformation as above
            id: deal.id,
            slug: deal.deal_slug,
            // ... rest of the transformation
          })),
          pagination: {
            currentPage: page,
            totalPages: Math.ceil(response.data.data.total / limit),
            total: response.data.data.total,
          },
        };
      }

      return {
        success: false,
        message: response.data.data.message || "Failed to fetch deals",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          "An error occurred while fetching deals",
      };
    }
  }

  //Todo => Method to get a deal by slug
  static async getDealBySlug(slug) {
    const token = this.getToken();
    const response = await axios.get(`${BASE_URL}/vendor/deals/${slug}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  }

  // Transforms a raw API deal object into the shape ViewOffer expects
  static transformDealObject(deal) {
    return {
      id: deal.id,
      slug: deal.deal_slug,
      title: deal.title,
      product_name: deal.product_name || "",
      product_category: deal.product_category || "",
      product_subcategory: deal.product_subcategory || "",
      category: deal.category || [],
      categories: deal.categories || [],
      subcategories: deal.subcategories || [],
      status: deal.status,
      image: deal.image,
      images: deal.images || [],
      companyLogo: deal.company_logo,
      companyName: deal.company_name,
      price: deal.price,
      discountedPrice: deal.discounted_price,
      discountPercentage: deal.discount_percentage,
      createdAt: deal.created_at,
      startDate: deal.start_date,
      expiryDate: deal.expiry_date,
      region_location: deal.region_location,
      partner_location: deal.partner_location,
      redemptionType: deal.redemption_type,
      sms_delivery_status: deal.sms_delivery_status,
      subscribers_count: deal.subscriber_count,
      initial_blast: deal.initial_blast,
      terms: deal.terms,
      refundPolicy: deal.refund_policy,
      details: {
        description: deal.description,
        redemptionInstructions: deal.redemption_instructions,
      },
      promote_deal: deal.promote_deal,
      deal_slug: deal.deal_slug,
      contact: {
        email: deal.contact?.email,
        phoneNumber: deal.phone_number,
        website: deal.online_link,
        socialMedia: deal.contact?.social_media,
      },
      gender: deal.gender,
      sms_content: deal.sms_content,
      sms_count: deal.sms_count,
      vendor_id: deal.vendor_id,
      target_branches: deal.target_branches,
      redemption_region_location: deal.redemption_region_location,
      redemption_branches: deal.redemption_branches,
      redemption_partners: deal.redemption_partners,
      redemption_limits: deal.redemption_limits,
      exclusions: deal.exclusions || [],
      sku: deal.sku,
      properties: deal.properties || [],
      age: deal.age || [],
      delivery_fee: deal.delivery_fee,
      free_delivery_areas: deal.free_delivery_areas || [],
      offer_type: deal.offer_type,
      reject_reason: deal.reject_reason,
      rejected_date: deal.rejected_date,
      deleted_at: deal.deleted_at,
      quantity: deal.quantity,
      sizes: deal.sizes || [],
      bundle: deal.bundle || [],
      bogo: deal.bogo || [],
      validity_type: deal.validity_type,
      flash: deal.flash || [],
    };
  }

  /**
   * Partial stock update via the same deal update endpoint.
   * Sends quantity, and sizes only when the product has size variants.
   */
  static async updateInventory(slug, { quantity, sizes } = {}) {
    try {
      const token = this.getToken();
      const payload = {
        quantity: Math.max(0, Math.floor(Number(quantity) || 0)),
      };

      if (Array.isArray(sizes) && sizes.length > 0) {
        payload.sizes = sizes.map((row) => ({
          size: row.size,
          quantity: String(Math.max(0, Math.floor(Number(row.quantity) || 0))),
        }));
      }

      const response = await axios.patch(
        `${BASE_URL}/vendor/deals/${slug}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      const envelope = response.data?.data;
      if (envelope?.status_code === "000" && !envelope?.in_error) {
        return { success: true, data: envelope };
      }

      return {
        success: false,
        message: envelope?.reason || envelope?.message || "Failed to update stock",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.data?.reason ||
          error.response?.data?.message ||
          error.message ||
          "Failed to update stock",
      };
    }
  }

  // Fetches a deal by slug and returns it in the transformed shape ViewOffer expects
  static async getDealBySlugTransformed(slug) {
    try {
      const token = this.getToken();
      const response = await axios.get(`${BASE_URL}/vendor/deals/${slug}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      const apiResp = response.data?.data;
      if (apiResp?.status_code === "000" && !apiResp?.in_error) {
        return { success: true, data: DealsRequest.transformDealObject(apiResp.data) };
      }
      return { success: false, message: apiResp?.reason || apiResp?.message || "Failed to fetch deal" };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.data?.reason || error.response?.data?.message || "Failed to fetch deal",
      };
    }
  }

  //Todo => Helper method to validate deal data before submission
  static validateDealData(formData) {
    const errors = {};

    //* Required fields (excluding expiry_date which is conditionally required)
    const requiredFields = [
      "title",
      "category",
      "start_date",
      "redemption_type",
      "phone_number",
      "terms_and_conditions",
      "description",
    ];

    requiredFields.forEach((field) => {
      if (
        !formData[field] ||
        (Array.isArray(formData[field]) && formData[field].length === 0) ||
        (typeof formData[field] === "string" && !formData[field].trim())
      ) {
        errors[field] = `${field.replace("_", " ")} is required`;
      }
    });

    //* Validate expiry_date only for regular deals, not flash sales
    if (formData.validity_type !== "flash_sale") {
      if (
        !formData.expiry_date ||
        (typeof formData.expiry_date === "string" && !formData.expiry_date.trim())
      ) {
        errors.expiry_date = "expiry date is required";
      }
    }

    //* Validate region_location
    if (!formData.region_location || formData.region_location.length === 0) {
      errors.region_location = "At least one region and location is required";
    } else {
      formData.region_location.forEach((item, index) => {
        if (!item.region || !item.location) {
          errors[`region_location_${index}`] =
            "Both region and location must be specified";
        }
      });
    }

    //* Validate dates (only for regular deals)
    if (formData.validity_type !== "flash_sale" && formData.expiry_date) {
      const startDate = new Date(formData.start_date);
      const expiryDate = new Date(formData.expiry_date);
      if (expiryDate <= startDate) {
        errors.expiry_date = "Expiry date must be after start date";
      }
    }

    //* Only validate prices if they are provided
    if (formData.price || formData.discounted_price) {
      const price = parseFloat(formData.price);
      const discountedPrice = parseFloat(formData.discounted_price);

      if (formData.price && (isNaN(price) || price < 0)) {
        errors.price = "Price must be 0 or greater";
      }

      if (
        formData.discounted_price &&
        (isNaN(discountedPrice) || discountedPrice < 0)
      ) {
        errors.discounted_price = "Discounted price must be 0 or greater";
      }

      if (price && discountedPrice && discountedPrice > price) {
        errors.discounted_price =
          "Discounted price cannot be greater than original price";
      }
    }

    //* Validate phone number
    if (!/^233\d{9}$/.test(formData.phone_number)) {
      errors.phone_number =
        "Phone number must start with 233 followed by 9 digits";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }

  static base64ToImage(base64String) {
    // Return default image if base64 is null/empty
    if (!base64String) {
      return "/default-image.png";
    }

    // If it's already a complete data URL, return as is
    if (base64String.startsWith("data:image")) {
      return base64String;
    }

    // Add data URL prefix if missing
    return `data:image/png;base64,${base64String}`;
  }

  // Add a method to get the current token
  static getToken() {
    return localStorage.getItem("dealboxx_token");
  }

  static async updateDeal(slug, formData) {
    try {
      // Optimize primary image if it's a File
      let optimizedImage = null;
      if (formData.image instanceof File) {
        optimizedImage = await this.optimizeImage(formData.image);
      }

      // Optimize gallery images only if provided as Files; keep strings as-is
      let optimizedImages = [];
      if (formData.images && Array.isArray(formData.images) && formData.images.length > 0) {
        const processed = await Promise.all(
          formData.images.map(async (img) => {
            if (img instanceof File) {
              try {
                return await this.optimizeImage(img);
              } catch (e) {
                console.error("Gallery image optimization failed:", e);
                return null;
              }
            }
            return img; // already a string/base64
          })
        );
        optimizedImages = processed.filter((v) => v !== null);
      }

      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(d.getDate()).padStart(2, "0")} ${String(
          d.getHours()
        ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(
          d.getSeconds()
        ).padStart(2, "0")}`;
      };

      // Process region_location to handle custom locations
      const processedRegionLocation = formData.region_location && Array.isArray(formData.region_location)
        ? formData.region_location.map((item) => ({
            ...item,
            location: item.location === "other" ? item.customLocation : item.location,
          }))
        : [];

      // Process redemption_region_location to handle custom locations
      const processedRedemptionRegionLocation = formData.redemption_region_location && Array.isArray(formData.redemption_region_location)
        ? formData.redemption_region_location.map((item) => ({
            ...item,
            location: item.location === "other" ? item.customLocation : item.location,
          }))
        : [];

      // Process free_delivery_areas to handle custom locations (and allow "Nationwide")
      const processedFreeDeliveryAreas = formData.free_delivery_areas && Array.isArray(formData.free_delivery_areas)
        ? formData.free_delivery_areas.map((item) => {
            if (typeof item === "string") return item;
            return {
              ...item,
              location: item.location === "other" ? item.customLocation : item.location,
            };
          })
        : [];

      // Normalize properties to {name, value}
      const processedProperties = formData.properties && Array.isArray(formData.properties)
        ? formData.properties
            .filter((p) => (p.key || p.name) && p.value)
            .map((p) => ({ name: p.name || p.key, value: p.value }))
        : [];

      // Ensure redemption_limits are strings
      const processedRedemptionLimits = formData.redemption_limits && Array.isArray(formData.redemption_limits)
        ? formData.redemption_limits.map((l) => String(l))
        : [];

      // Wrap structured fields as arrays of objects (backend format)
      const flashData = [
        {
          flash_start_time: formData.flash_start_time || null,
          flash_end_time: formData.flash_end_time || null,
          flash_start_date: formData.flash_start_date || null,
          flash_duration: formData.flash_duration || null,
        },
      ];

      const bundleData = [
        {
          original_sum: formData.original_sum || null,
          bundle_price: formData.bundle_price || null,
          bundle_savings: formData.bundle_savings || null,
          bundle_skus: formData.bundle_skus || [],
        },
      ];

      const bogoData = [
        {
          bogo_item_price: formData.offer_type === "bogo" ? formData.bogo_item_price || null : null,
          bogo_quantity: formData.offer_type === "bogo" ? formData.bogo_quantity || null : null,
          bogo_free_quantity: formData.offer_type === "bogo" ? formData.bogo_free_quantity || null : null,
        },
      ];

      const ageData = [
        {
          min_age: formData.min_age || null,
          max_age: formData.max_age || null,
          no_age_limit: formData.no_age_limit || false,
        },
      ];

      // Prepare the payload
      const payload = {
        ...formData,
        category: Array.isArray(formData.category) ? formData.category : [formData.category],
        image: optimizedImage || formData.image,
        images: optimizedImages,
        properties: processedProperties,
        redemption_limits: processedRedemptionLimits,
        flash: flashData,
        bundle: bundleData,
        bogo: bogoData,
        age: ageData,
        expiry_date: formatDate(formData.expiry_date),
        start_date: formatDate(formData.start_date),
        region_location: processedRegionLocation,
        redemption_region_location: processedRedemptionRegionLocation,
        free_delivery_areas: processedFreeDeliveryAreas,
        promote_deal: formData.promote_deal ? 1 : 0,
      };
      attachMarketplaceCategories(payload, formData);

      const token = this.getToken();
      const response = await axios.put(
        `${BASE_URL}/vendor/deals/${slug}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (
        response.data.data.status_code === "000" &&
        !response.data.data.in_error
      ) {
        return {
          success: true,
          data: response.data.data,
        };
      }

      throw new Error(response.data.data.reason || "Failed to update deal");
    } catch (error) {
      console.error("Update Deal Error:", error);
      throw new Error(
        error.response?.data?.message ||
          "An error occurred while updating the deal"
      );
    }
  }

  static async deleteDeal(slug) {
    const token = this.getToken();
    const response = await axios.delete(`${BASE_URL}/vendor/deals/${slug}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  //* Fetch Redemptions
  static async fetchRedemptions() {
    const token = this.getToken();
    const response = await axios.get(`${BASE_URL}/vendor/redeemed/deals`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  //* Complete Redemption
  static async completeRedemption(deal_slug) {
    const token = this.getToken();
    const response = await axios.patch(
      `${BASE_URL}/vendor/update/deal/completed/${deal_slug}`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  }

  //* Promote Offer
  static async promoteDeal(deal_slug) {
    const token = this.getToken();
    const response = await axios.get(
      `${BASE_URL}/vendor/promote/deal/${deal_slug}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );
    return response.data;
  }
  //* Demote Offer
  static async demoteDeal(deal_slug) {
    const token = this.getToken();
    console.log("token", token);
    const response = await axios.get(
      `${BASE_URL}/vendor/demote/deal/${deal_slug}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );
    return response.data;
  }

  //Todo => Test Deal Blast
  static async testDealBlast(payload, deal_slug) {
    const token = this.getToken();
    const response = await axios.post(
      `${BASE_URL}/vendor/test/blast/deal/${deal_slug}`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  }
}
